/**
 * 组织树数据管理Hook
 * 负责组织树的加载、创建、编辑、删除等操作
 */

import { useState, useCallback, useMemo } from "react";
import { message } from "antd";
import { organizationAPI } from "../../../../api/organizationApi";

const flattenTree = (nodes = []) => {
    const result = [];
    const seenIds = new Set();
    const walk = (items = [], level = 0, path = []) => {
        items.forEach((node) => {
            if (seenIds.has(node.id)) return;
            seenIds.add(node.id);
            const nextPath = [...path, node.name];
            result.push({
                ...node,
                level,
                pathLabel: nextPath.join(" / "),
            });
            if (Array.isArray(node.children) && node.children.length > 0) {
                walk(node.children, level + 1, nextPath);
            }
        });
    };
    walk(nodes);
    return result;
};

const findNodeById = (tree, id) => {
    for (const node of tree) {
        if (node.id === id) return node;
        if (node.children && node.children.length > 0) {
            const found = findNodeById(node.children, id);
            if (found) return found;
        }
    }
    return null;
};

const collectAncestorKeys = (tree, targetId) => {
    const path = [];
    const find = (nodes, trail) => {
        for (const node of nodes) {
            if (node.id === targetId) {
                path.push(...trail, String(node.id));
                return true;
            }
            if (node.children && node.children.length > 0) {
                if (find(node.children, [...trail, String(node.id)])) {
                    return true;
                }
            }
        }
        return false;
    };
    find(tree, []);
    return path;
};

export const useOrganizationTree = () => {
    const [loading, setLoading] = useState(false);
    const [organizationTree, setOrganizationTree] = useState([]);

    // 平面化列表，用于快速查找
    const organizationFlatList = useMemo(() => flattenTree(organizationTree), [organizationTree]);

    // 加载组织树
    const loadOrganizationTree = useCallback(async () => {
        try {
            setLoading(true);
            const response = await organizationAPI.getOrganizationTree();
            setOrganizationTree(response || []);
        } catch (error) {
            message.error("加载组织树失败");
            console.error("Load organization tree error:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // 创建组织
    const createOrganization = useCallback(async (values) => {
        try {
            const response = await organizationAPI.createOrganization(values);
            if (response.success) {
                message.success(response.message || "组织创建成功");
                await loadOrganizationTree();
                return response;
            } else {
                throw new Error(response.message || "创建组织失败");
            }
        } catch (error) {
            message.error(error.response?.data?.detail || error.message || "创建组织失败");
            throw error;
        }
    }, [loadOrganizationTree]);

    // 更新组织
    const updateOrganization = useCallback(async (orgId, values) => {
        try {
            const response = await organizationAPI.updateOrganization({ id: orgId, data: values });
            if (response.success) {
                message.success(response.message || "组织更新成功");
                await loadOrganizationTree();
                return true;
            } else {
                throw new Error(response.message || "更新组织失败");
            }
        } catch (error) {
            message.error(error.response?.data?.detail || error.message || "更新组织失败");
            throw error;
        }
    }, [loadOrganizationTree]);

    // 删除组织
    const deleteOrganization = useCallback(async (orgId) => {
        try {
            const response = await organizationAPI.deleteOrganization(orgId);
            if (response.success) {
                message.success(response.message || "组织删除成功");
                await loadOrganizationTree();
                return true;
            } else {
                throw new Error(response.message || "删除组织失败");
            }
        } catch (error) {
            message.error(error.response?.data?.detail || error.message || "删除组织失败");
            throw error;
        }
    }, [loadOrganizationTree]);

    return {
        loading,
        organizationTree,
        organizationFlatList,
        loadOrganizationTree,
        createOrganization,
        updateOrganization,
        deleteOrganization,
        findNodeById: useCallback((id) => findNodeById(organizationTree, id), [organizationTree]),
        collectAncestorKeys: useCallback((id) => collectAncestorKeys(organizationTree, id), [organizationTree]),
    };
};
